import React, { useEffect, useLayoutEffect, useRef } from "react";

import defaultCover from "@assets/images/news_default_cover.jpg";

import "./index.less";

/** 设计稿基础尺寸：所有内部 px 都按这个画布写，再整体 scale */
const BASE_WIDTH = 423;
const BASE_HEIGHT = 282;

/**
 * 资讯 / 产品卡片封面
 *
 * - 设计尺寸 423×282（3:2），宽度由父容器决定，高度按 3:2 算出来；
 * - 内部固定 423×282 画布，按「未缩放的布局宽度 / 423」整体 scale；Pad 模式下
 *   父级 .ow-scale-root 的 CSS zoom 由父级统一处理，这里不能使用 zoom 后的视觉宽度，
 *   否则封面会被 zoom 二次缩放（横屏 1366 / 竖屏 1024 这类 zoom=1 的宽度才刚好）；
 *   所以 padding、字号、置顶标记等所有 px 值都直接按设计稿写，不需要逐个换 unit；
 * - 背景图默认用 src/assets/images/news_default_cover.jpg；
 * - 文字只叠在图上，不加压暗遮罩（按需求确认）。
 *
 * @param {string} brandName 品牌名称，默认 BUJUE
 * @param {string} typeName 类型名称；为空时左上角只显示品牌
 * @param {string} title 资讯标题，最多两行；传了 coverUrl 时不渲染
 * @param {string} coverUrl 背景图地址；有值时只显示背景图（不显示标题），为空时用默认图 + 标题
 * @param {boolean} pinned 是否置顶，右上角显示「置顶」标记
 * @param {boolean} listMode 列表模式：默认 false；只有列表模式才渲染左上角品牌/分类和右上角置顶
 */
export default function NewsCardCover({
  brandName = "BUJUE",
  typeName = "",
  title = "",
  coverUrl = "",
  pinned = false,
  listMode = false,
  className,
  onCoverError,
  ...rest
}) {
  const rootRef = useRef(null);

  const brand = String(brandName || "").trim() || "BUJUE";
  const type = String(typeName || "").trim();
  const brandLine = type ? brand + "·" + type : brand;
  const customCover = String(coverUrl || "").trim();
  const background = customCover || defaultCover;
  const titleText = String(title || "");
  /** 传了 coverUrl 就只展示背景图，不再渲染标题（没有传时才走默认图 + 标题） */
  const showTitle = !customCover && Boolean(titleText.trim());
  /** 非列表模式（如详情页相似推荐）只展示封面图，不展示品牌/分类/置顶 */
  const showBrand = listMode;
  const showPin = listMode && pinned;

  /** 宽度变化时只更新 CSS 变量，不触发 React re-render */
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const applyScale = () => {
      const cs = window.getComputedStyle(root);
      /* 取「未缩放、带小数」的布局宽度：
       * - clientWidth 是取整后的值（卡片 457.429 会读到 457），scale 按 457 算，
       *   封面就比卡片窄 0.43px，右/下边缘会露一条缝；
       * - getBoundingClientRect().width 在 Pad 的 .ow-scale-root CSS zoom 下是缩放后的
       *   视觉宽度，而 canvas 的 transform 会在 zoom 坐标系里再缩一次（二次缩放，
       *   只有 zoom=1 时刚好），所以也不能用；
       * - computed width 是未缩放的布局宽度、保留小数；再加左右 padding，
       *   因为绝对定位的 canvas 贴的是 padding box（即 clientWidth 的语义）。 */
      const contentWidth = parseFloat(cs.width);
      const width = Number.isFinite(contentWidth)
        ? contentWidth +
          (parseFloat(cs.paddingLeft) || 0) +
          (parseFloat(cs.paddingRight) || 0)
        : root.clientWidth;
      if (!width) return;
      root.style.setProperty("--ncc-scale", String(width / BASE_WIDTH));
    };

    applyScale();

    if (typeof ResizeObserver === "function") {
      const observer = new ResizeObserver(applyScale);
      observer.observe(root);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", applyScale);
    return () => window.removeEventListener("resize", applyScale);
  }, []);

  const rootClassName = [
    "news-card-cover",
    showPin ? "is-pinned" : "",
    className || "",
  ]
    .filter(Boolean)
    .join(" ");

  /* 封面图改为卡片自身的背景（见 index.less）。
   * CSS 背景不能挂 onError，这里用一次同 src 的 Image 预检：保留 onCoverError
   * 回调能力，同时把图预热进缓存，背景基本立即上屏。
   * 预检不覆盖 referrer 策略，与背景那次请求保持一致 —— 封面图来自公司 OSS，
   * 没有防盗链，不需要 no-referrer；策略一致，预检的成败才等于背景的成败。 */
  useEffect(() => {
    if (!background) return undefined;
    let cancelled = false;
    const probe = new Image();
    probe.onerror = (event) => {
      if (!cancelled) onCoverError?.(event);
    };
    probe.src = background;
    return () => {
      cancelled = true;
    };
  }, [background, onCoverError]);

  return (
    <div
      ref={rootRef}
      className={rootClassName}
      {...rest}
      /* 与 rest.style 合并，封面地址来自 props，直接作为卡片背景 */
      style={{ ...(rest.style || {}), backgroundImage: `url("${background}")` }}
    >
      <div className="news-card-cover__canvas">
        <div className="news-card-cover__content">
          {showBrand ? (
            <div className="news-card-cover__brand">{brandLine}</div>
          ) : null}
          {showTitle ? (
            <div className="news-card-cover__title">{titleText}</div>
          ) : null}
        </div>

        {showPin ? (
          <span className="news-card-cover__pin">置顶</span>
        ) : null}
      </div>
    </div>
  );
}

export { BASE_WIDTH, BASE_HEIGHT };
